import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { getComercialInvoicePDF } from "@/redux/resources/resources.action";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { Typography } from "@mui/material";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Download, LoaderCircle } from "lucide-react";
import { useState } from "react";

export type ComercialInvoicingColumns = {
  numero: string;
  fecha: string;
  condicionVenta: string;
  nroInterno: string;
};

export function useComercialInvoicingColumns(): ColumnDef<ComercialInvoicingColumns>[] {
  const dispatch = useAppDispatch();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const handleDownloadPDF = async (row: ComercialInvoicingColumns) => {
    const comercialInvoiceNro = row.nroInterno;
    setLoadingId(comercialInvoiceNro);
    try {
      const result = await dispatch(
        getComercialInvoicePDF(comercialInvoiceNro),
      );
      if (result.payload) {
        window.open(result.payload as string, "_blank");
        SuccessToast({ message: "El archivo se ha descargado correctamente" });
      } else {
        ErrorToast({
          message:
            "No se ha podido descargar el archivo, favor de contactar a un administrador",
        });
      }
    } catch (error) {
      ErrorToast({
        message:
          "No se ha podido descargar el archivo, favor de contactar a un administrador",
      });
    } finally {
      setLoadingId(null);
    }
  };
  const isMobile = useIsMobile();

  const columnsComercialInvoicing: ColumnDef<ComercialInvoicingColumns>[] = [
    {
      accessorKey: "numero",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            {isMobile ? "Nro. factura" : "Número de factura"}
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<ComercialInvoicingColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="numero">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.numero || "- - -"}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "fecha",
      header: () => (
        <div className="text-left ">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            {isMobile ? "Fecha fac." : "Fecha de factura"}
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<ComercialInvoicingColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="fecha">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.fecha || "- - -"}
            </Typography>
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "condicionVenta",
      header: () => (
        <div className="text-left">
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Condición de venta
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<ComercialInvoicingColumns> }) => {
        return (
          <div className="text-left font-medium" aria-label="condicionVenta">
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.condicionVenta || "- - -"}
            </Typography>
          </div>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }: { row: Row<ComercialInvoicingColumns> }) => {
        const isRowLoading = loadingId === row.original.nroInterno;
        return (
          <div
            className="text-left font-medium"
            aria-label="actions-download-file"
          >
            {!isRowLoading && row.original.numero && (
              <Button
                variant="icon"
                size="icon"
                onClick={() => {
                  handleDownloadPDF(row.original);
                }}
              >
                <Download />
              </Button>
            )}
            {isRowLoading && (
              <div className="flex items-center justify-center">
                <LoaderCircle className="h-6 w-6 animate-spin text-gray-500" />
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<ComercialInvoicingColumns>[];
  return columnsComercialInvoicing;
}
